/*
 * INTEX CUSTOM — not part of vanilla Ignite. See INTEX-CHANGES.md §14.27.
 *
 * Drives <intex-range-picker> (snippets/intex-range-picker.liquid): the two
 * step Year -> Model control on a range page, scoped to the models whose
 * `parents` include the collection it is rendered on.
 *
 * Data comes from the same `<collection>?view=intex-models` endpoint the
 * header picker uses, under a cache key of its own — assets/intex-compat-picker.js
 * is not touched, and neither script can invalidate the other's cache. The
 * endpoint now emits `parents`, the model's parent_collections handles.
 *
 * SCOPE: only models whose `parent_collections` includes this collection,
 * matched on the `parents` handles the endpoint emits.
 *
 * This reverses §14.29, which scoped on the collection's own compat_models
 * filter values. Those are every model that ANY product in the collection
 * fits, so one universal part drags unrelated models in — SUP Board Parts
 * listed spa models, Challenger Boat Parts listed Mariner models. The live
 * endpoint carries parent_collections on 270 of 278 models, and the parent
 * rule gives both collections exactly the right list, so the filter-value
 * scope is gone rather than kept as a fallback: it was the bug.
 *
 * Rows 3 and 4 of the mode table (§14.40) are settled here, because this is
 * the only place that knows whether any model names this collection: models
 * found reveals the picker, none found reveals the trust rows beside it, and
 * neither available removes the column.
 *
 * Year control: up to three years render as a segmented radio group, four or
 * more as a <select>. "All" is always the first option and is the default.
 *
 * No library, no framework. The only progressive-enhancement promise the
 * markup makes is that the steps stay `hidden` until there is data to put in
 * them, so a failure leaves the heading and the browse-all link rather than
 * two dead controls.
 */
(function () {
  const CACHE_KEY = 'intexRangeModels:v1';

  /* TEMPORARY — see the debug block in snippets/intex-range-picker.liquid.
     ?intex_debug=1 reveals a box in the panel with everything the picker saw,
     for checking a range page without a console. Remove both together. */
  const DEBUG = new URLSearchParams(window.location.search).get('intex_debug') === '1';
  const debugRows = [];

  function report(key, value) {
    debugRows.push([key, String(value)]);
    if (!DEBUG) return;

    const box = document.querySelector('[data-intex-range-debug]');
    const list = document.querySelector('[data-intex-range-debug-list]');
    if (!box || !list) return;

    box.hidden = false;
    list.textContent = '';
    debugRows.forEach(([rowKey, rowValue]) => {
      const dt = document.createElement('dt');
      dt.textContent = rowKey;
      const dd = document.createElement('dd');
      dd.textContent = rowValue;
      list.append(dt, dd);
    });
  }

  report('script loaded', 'yes');
  const MODEL_PARAM = 'filter.p.m.custom.compat_models';
  /* What compatibility-finder.js write() dispatches on document after it saves,
     and what assets/compatibility-badge.js and assets/intex-finder-cta.js
     re-render the current page from. */
  const CHANGE_EVENT = 'compatibility-finder:change';
  const SEGMENTED_MAX = 3;

  function readCache() {
    try {
      const raw = window.sessionStorage.getItem(CACHE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed.models) ? parsed.models : null;
    } catch (error) {
      return null;
    }
  }

  function writeCache(models) {
    try {
      window.sessionStorage.setItem(CACHE_KEY, JSON.stringify({ models }));
    } catch (error) {
      /* Private mode, or the quota is full. Not worth failing the picker. */
    }
  }

  /* Mirrors intex-compat-picker.js modelsRequestUrl(): resolved against the
     origin, `view` always re-stamped, `preview_theme_id` forwarded, and no
     Accept header — Shopify answers `Accept: application/json` on a collection
     URL with its own JSON instead of rendering the `?view=` template. */
  function modelsRequestUrl(base) {
    const url = new URL(base || '/collections/all-parts', window.location.origin);
    url.searchParams.set('view', 'intex-models');

    const preview = new URLSearchParams(window.location.search).get('preview_theme_id');
    if (preview) url.searchParams.set('preview_theme_id', preview);

    return url.toString();
  }

  async function loadModels(base) {
    const cached = readCache();
    if (cached) {
      report('source', `sessionStorage (${CACHE_KEY})`);
      report('models returned', cached.length);
      return cached;
    }

    const url = modelsRequestUrl(base);
    report('fetch url', url);

    const response = await fetch(url);
    report('http status', `${response.status} ${response.statusText}`);
    report('content-type', response.headers.get('content-type') || '(none)');

    if (!response.ok) throw new Error(`HTTP ${response.status} fetching ${url}`);

    const body = await response.text();
    let parsed;
    try {
      parsed = JSON.parse(body);
    } catch (error) {
      const type = response.headers.get('content-type') || 'no content-type';
      report('first 120 chars', body.slice(0, 120).replace(/\s+/g, ' ').trim());
      throw new Error(`${url} did not return JSON (${type}). It is probably not a collection URL, or collection.intex-models.liquid is not on the theme.`);
    }

    if (!Array.isArray(parsed.models)) throw new Error(`${url} has no "models" array.`);

    report('endpoint total', parsed.total);
    report('endpoint rendered', parsed.rendered);
    report('models returned', parsed.models.length);

    if (parsed.models.length) writeCache(parsed.models);
    return parsed.models;
  }

  function label(model) {
    return (model.name || '').trim() || model.id;
  }

  class IntexRangePicker extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;

      this.handle = this.dataset.collection || '';

      /* Row 2 won. The inline script in sections/intex-main-collection.liquid
         hid this whole fallback wrapper before first paint because the URL
         carries a model, and the promoted model panel is in the column instead.
         Bail: filling a hidden card is wasted, and resolveMode(false) would
         otherwise remove the very column the model panel is sitting in. */
      if (this.closest('[data-intex-mc-fallback]')?.hidden) {
        report('mode', 'row 2 — model panel promoted, picker stands down');
        return;
      }

      this.resultsUrl = this.dataset.resultsUrl || '/collections/all-parts';
      /* This section's "Show the search box from". Only the fallback: the
         header finder's own threshold wins when it is on the page — see
         searchThreshold(). */
      this.searchMin = parseInt(this.dataset.searchMin, 10) || 15;
      this.allLabel = this.dataset.allLabel || 'All';

      this.countEl = this.querySelector('[data-range-count]');
      this.yearStep = this.querySelector('[data-range-step="year"]');
      this.yearsEl = this.querySelector('[data-range-years]');
      this.yearLabelId = this.yearStep?.querySelector('.intex-range__label')?.id || '';
      this.modelStep = this.querySelector('[data-range-step="model"]');
      this.comboEl = this.querySelector('[data-range-combo]');
      this.controlEl = this.querySelector('[data-range-control]');
      this.controlText = this.querySelector('[data-range-control-text]');
      this.controlSub = this.querySelector('[data-range-control-sub]');
      this.controlMedia = this.querySelector('[data-range-control-media]');
      this.searchWrap = this.querySelector('[data-range-search]');
      this.searchInput = this.querySelector('[data-range-search-input]');
      this.listEl = this.querySelector('[data-range-list]');
      /* The header picker's .intex-cp__panel around the listbox (§27). It is
         what opens and closes; the listbox inside it is never hidden. */
      this.panelEl = this.querySelector('[data-range-panel]') || this.listEl;
      this.noPhoto = this.querySelector('template[data-range-no-photo]');
      this.emptyEl = this.querySelector('[data-range-empty]');
      this.selectEl = this.querySelector('[data-range-select]');
      this.submitEl = this.querySelector('[data-range-submit]');
      this.errorEl = this.querySelector('[data-range-error]');

      /* The trust rows that share the swap box with this card, and the column
         itself — both needed for the row 3 / row 4 decision in build(). */
      this.trustEl = document.querySelector('[data-range-trust]');
      this.noteEl = document.querySelector('[data-range-note]');
      this.asideEl = this.closest('[data-intex-mc-aside]');

      report('element found', 'yes');
      report('collection', this.handle || '(none)');
      report('scoped by', 'parent_collections');

      if (!this.selectEl || !this.submitEl || !this.controlEl || !this.controlText || !this.listEl) {
        report('error', 'the model field, its listbox, the value select or the submit button is missing from the markup');
        return;
      }

      this.year = '';
      this.activeIndex = -1;
      this.syncFinderRadius();
      this.visible = [];

      loadModels(this.dataset.modelsUrl)
        .then(models => this.build(models))
        .catch(error => {
          /* A failed fetch must still settle the mode, or the pending card
             would sit there promising controls that never arrive. It resolves
             the same way as an empty scope: trust rows, or no column. */
          report('error', error.message);
          console.warn('[IntexRangePicker] could not load model data.', error);
          this.resolveMode(false);
        });
    }

    /* Rows 3 and 4 of the mode table: the picker when this collection is named
       as a parent, the trust rows when it is not, the column gone when there is
       neither. One place, one decision, nothing left half-shown. */
    resolveMode(found) {
      if (found) {
        this.hidden = false;
        this.classList.remove('intex-range--pending');
        if (this.trustEl) this.trustEl.hidden = true;
        report('mode', 'row 3 — picker');
        return;
      }

      this.hidden = true;

      if (this.trustEl) {
        this.trustEl.hidden = false;
        report('mode', 'row 4 — trust points');
        return;
      }

      /* A Shop by type block (§33) still has something to say, so the
         panel stays with just that in it. */
      if (this.asideEl && this.asideEl.querySelector('[data-intex-shop-type]')) {
        report('mode', 'row 4 — shop by type only');
        return;
      }

      /* Row 5. No models and no trust rows, so the column has nothing left to
         say and the grid takes the full width — the same end state the server
         renders when the picker is switched off and there are no trust blocks.
         This is the one case that can move the layout after load, and it only
         happens on a collection with neither authored models nor trust blocks,
         which the editor note names. */
      if (this.asideEl && !this.asideEl.querySelector('[data-intex-model-panel]:not([hidden])')) {
        this.asideEl.remove();
        const grid = document.querySelector('.intex-mc__grid');
        if (grid) grid.classList.add('intex-mc__grid--full');
      }
      report('mode', 'row 5 — no column');
    }

    build(allModels) {
      this.models = allModels.filter(model =>
        Array.isArray(model.parents) && model.parents.indexOf(this.handle) !== -1
      );

      report('models loaded', allModels.length);
      report('named this collection as parent', this.models.length);

      if (!this.models.length) {
        const authored = allModels.filter(model => Array.isArray(model.parents) && model.parents.length).length;
        report('error', `no model names "${this.handle}" in parent_collections`);
        report('models carrying any parent', `${authored} of ${allModels.length}`);
        console.warn('[IntexRangePicker] no model names this collection in parent_collections.', {
          collection: this.handle,
          modelsLoaded: allModels.length,
          modelsWithAnyParent: authored
        });
        this.resolveMode(false);
        return;
      }

      this.resolveMode(true);

      if (this.noteEl) {
        this.noteEl.textContent = `Range picker: ${this.models.length} model${this.models.length === 1 ? '' : 's'} name ${this.handle} in Parent collections.`;
      }

      this.models.sort((a, b) => label(a).localeCompare(label(b), undefined, { numeric: true }));

      if (this.countEl) {
        const n = this.models.length;
        this.countEl.textContent = `${n} ${n === 1 ? 'model' : 'models'}`;
        this.countEl.hidden = false;
      }

      this.buildYears();
      this.renderModels();

      this.bindCombo();
      this.submitEl.addEventListener('click', () => this.submit());
      this.syncSubmit();

      /* Back / forward cache: a page restored after Show my parts would come
         back with the button still spinning. Put it back as it was. */
      window.addEventListener('pageshow', event => {
        if (event.persisted) this.setLoading(false);
      });
    }

    /* ------------------------------------------------------- MODEL FIELD */

    /* The header finder's model field, behaviour for behaviour (§14.70):
       openDropdown(), closeDropdown(), commit(), applySearch() and
       onFieldKeyDown() in assets/intex-compat-picker.js. A button shows the
       chosen model or the placeholder and opens the list; nothing can be typed
       into the field itself. For longer lists a search box shows above the
       list, at the header finder's own threshold. Search is
       String.prototype.includes, never new RegExp(query) — a model name with
       '(' or '+' in it would throw mid-keystroke. That is invariant 2 in
       INTEX-CHANGES.md §1. The hidden <select> holds the chosen GID so
       submit() reads one value from one place. */
    bindCombo() {
      this.controlEl.addEventListener('click', event => {
        event.preventDefault();
        if (this.isOpen()) this.closeList();
        else this.openList();
      });

      /* One handler for the button and the search box, as the header binds it
         on the whole field. */
      this.comboEl.addEventListener('keydown', event => this.onComboKey(event));

      this.searchInput?.addEventListener('input', () => this.applySearch());

      this.listEl.addEventListener('click', event => {
        const option = event.target.closest('[data-range-option]');
        if (!option) return;
        event.preventDefault();
        this.commit(Number(option.dataset.index));
      });

      /* Keeps focus where it is while the pointer is down on a row. */
      this.listEl.addEventListener('mousedown', event => event.preventDefault());

      document.addEventListener('pointerdown', event => {
        if (!this.comboEl.contains(event.target)) this.closeList();
      });
    }

    isOpen() {
      return !this.panelEl.hidden;
    }

    /* Escape, Enter, Tab, Up / Down, Home / End: onFieldKeyDown(). */
    onComboKey(event) {
      const isOpen = this.isOpen();

      if (event.key === 'Escape') {
        if (!isOpen) return;
        /* Stop the finder's document-level Escape handler from closing the
           header panel when only this list should close. */
        event.stopPropagation();
        event.preventDefault();
        this.closeList();
        this.controlEl.focus();
        return;
      }

      if (event.key === 'Enter') {
        event.preventDefault();
        if (!isOpen) {
          this.openList();
          return;
        }
        const active = this.listEl.querySelector('[data-range-option].is-active');
        if (active) this.commit(Number(active.dataset.index));
        return;
      }

      if (event.key === 'Tab') {
        this.closeList();
        return;
      }

      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        if (!isOpen) {
          this.openList();
          return;
        }
        this.moveActive(event.key === 'ArrowDown' ? 1 : -1);
        return;
      }

      if ((event.key === 'Home' || event.key === 'End') && isOpen) {
        event.preventDefault();
        const options = this.visibleOptions();
        const target = event.key === 'Home' ? options[0] : options[options.length - 1];
        if (target) this.setActive(Number(target.dataset.index));
      }
    }

    openList() {
      if (!this.visible.length) return;

      this.panelEl.hidden = false;
      this.controlEl.setAttribute('aria-expanded', 'true');
      this.comboEl.classList.add('is-open');

      if (this.searchInput && !this.searchWrap.hidden) {
        this.searchInput.value = '';
        this.applySearch();
        this.searchInput.focus();
      }

      const selected = this.listEl.querySelector('[data-range-option].is-selected');
      if (selected) this.setActive(Number(selected.dataset.index));
    }

    closeList() {
      if (!this.isOpen()) return;
      this.panelEl.hidden = true;
      this.controlEl.setAttribute('aria-expanded', 'false');
      this.comboEl.classList.remove('is-open');
      this.setActive(-1);
    }

    visibleOptions() {
      return Array.from(this.listEl.querySelectorAll('[data-range-option]:not([hidden])'));
    }

    moveActive(step) {
      const options = this.visibleOptions();
      if (!options.length) return;
      const current = options.findIndex(option => option.classList.contains('is-active'));
      let next = current + step;
      if (next < 0) next = options.length - 1;
      if (next >= options.length) next = 0;
      this.setActive(Number(options[next].dataset.index));
    }

    /* The active row is announced through aria-activedescendant on whichever
       element has focus: the search box when it is showing, else the button. */
    setActive(index) {
      this.activeIndex = index;

      this.listEl.querySelectorAll('[data-range-option]').forEach(option => {
        const isActive = Number(option.dataset.index) === index;
        option.classList.toggle('is-active', isActive);
        if (isActive) option.scrollIntoView({ block: 'nearest' });
      });

      const option = index > -1 ? this.listEl.querySelector(`[data-range-option][data-index="${index}"]`) : null;
      [this.controlEl, this.searchInput].forEach(element => {
        if (!element) return;
        if (option) element.setAttribute('aria-activedescendant', option.id);
        else element.removeAttribute('aria-activedescendant');
      });
    }

    applySearch() {
      const query = (this.searchInput?.value || '').trim().toLowerCase();
      let shown = 0;

      this.listEl.querySelectorAll('[data-range-option]').forEach(option => {
        const model = this.visible[Number(option.dataset.index)];
        const haystack = model ? `${label(model)} ${model.number || ''} ${model.size || ''} ${model.year || ''}`.toLowerCase() : '';
        const isMatch = !query || haystack.includes(query);
        option.hidden = !isMatch;
        if (isMatch) shown += 1;
      });

      if (this.emptyEl) this.emptyEl.hidden = shown > 0;
      this.setActive(-1);
    }

    commit(index) {
      const model = this.visible[index];
      if (!model) return;

      this.chosen = model;
      this.selectEl.textContent = '';

      const option = document.createElement('option');
      option.value = model.id;
      option.textContent = label(model);
      this.selectEl.appendChild(option);
      this.selectEl.value = model.id;

      this.listEl.querySelectorAll('[data-range-option]').forEach(node => {
        const isSelected = Number(node.dataset.index) === index;
        node.classList.toggle('is-selected', isSelected);
        node.setAttribute('aria-selected', isSelected ? 'true' : 'false');
      });

      this.repaint();
      this.closeList();
      this.controlEl.focus();
      this.syncSubmit();
    }

    /* repaint() in the header: the chosen model's thumbnail, name and
       size · year line on the button, or the placeholder. */
    repaint() {
      const model = this.chosen;

      this.controlText.textContent = model ? label(model) : (this.controlText.dataset.placeholder || '');
      this.controlEl.classList.toggle('is-empty', !model);

      if (this.controlSub) {
        const sub = model
          ? [model.size, model.year].map(part => (part || '').trim()).filter(Boolean).join(' · ')
          : '';
        this.controlSub.textContent = sub;
        this.controlSub.hidden = !sub;
      }

      if (this.controlMedia) {
        this.controlMedia.textContent = '';
        this.controlMedia.hidden = !model;
        if (model) this.controlMedia.appendChild(this.buildThumb(model));
      }
    }

    /* The header finder's thumbnails take their corners from the finder
       block's corner radius: compatibility-finder.liquid writes the block's
       Custom corner radius into --inputs-radius on its panel (when "Use
       default corner radius" is off), and intex-compat-picker.css rounds a
       thumb to 0.75 of it — a full circle from 27px up. A section cannot read
       a header block's settings in Liquid, so the resolved value is read off
       that panel here and set on this picker. Both lists then round from the
       same setting. With no finder on the page, the theme's own value stands. */
    syncFinderRadius() {
      const finderPanel = document.querySelector('.compatibility-finder__panel');
      if (!finderPanel) return;
      const styles = getComputedStyle(finderPanel);
      ['--inputs-radius', '--popup-corner-radius'].forEach(name => {
        const value = styles.getPropertyValue(name).trim();
        if (value) this.style.setProperty(name, value);
      });
    }

    /* The header shows its search box when the list is longer than the
       finder block's "Min items for search" (data-search-min-items on the
       header picker). The same number is read here so both lists show it at
       the same size; this section's own setting is only the fallback. */
    searchThreshold() {
      const header = document.querySelector('intex-compat-picker[data-search-min-items]');
      const value = header ? Number(header.dataset.searchMinItems) : NaN;
      if (Number.isFinite(value) && value >= 0) return value;
      return this.searchMin;
    }

    /* Years, newest first, blanks dropped. Up to three render as a segmented
       radio group; four or more would not fit the panel, so they become a
       <select>. "All" is always first and always the default. */
    buildYears() {
      const years = [];
      this.models.forEach(model => {
        const value = (model.year || '').trim();
        if (value && years.indexOf(value) === -1) years.push(value);
      });
      years.sort((a, b) => b.localeCompare(a, undefined, { numeric: true }));

      /* No years at all: drop the step rather than leave its shim standing in
         for a control that is never built. */
      if (!years.length) {
        this.yearStep.hidden = true;
        return;
      }

      this.yearsEl.textContent = '';
      const options = [{ value: '', text: this.allLabel }].concat(
        years.map(value => ({ value, text: value }))
      );

      if (years.length <= SEGMENTED_MAX) {
        const group = document.createElement('div');
        group.className = 'intex-range__segmented';
        group.setAttribute('role', 'radiogroup');
        /* The group and the select both borrow the step's own heading, so
           neither is an unnamed control. */
        if (this.yearLabelId) group.setAttribute('aria-labelledby', this.yearLabelId);

        options.forEach((option, index) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'intex-range__segment';
          button.setAttribute('role', 'radio');
          button.setAttribute('aria-checked', index === 0 ? 'true' : 'false');
          button.textContent = option.text;
          button.addEventListener('click', () => {
            this.year = option.value;
            group.querySelectorAll('[role="radio"]').forEach(node =>
              node.setAttribute('aria-checked', node === button ? 'true' : 'false')
            );
            this.onYearChange();
          });
          group.appendChild(button);
        });

        this.yearsEl.appendChild(group);
      } else {
        const select = document.createElement('select');
        select.className = 'intex-range__select';
        if (this.yearLabelId) select.setAttribute('aria-labelledby', this.yearLabelId);
        options.forEach(option => {
          const node = document.createElement('option');
          node.value = option.value;
          node.textContent = option.text;
          select.appendChild(node);
        });
        select.addEventListener('change', () => {
          this.year = select.value;
          this.onYearChange();
        });
        this.yearsEl.appendChild(select);
      }

    }

    /* A year change can put the chosen model out of scope, and a submit button
       live against a model the list no longer offers is a trap. Drop the
       choice when that happens and leave the field empty. */
    onYearChange() {
      if (this.chosen && this.year && (this.chosen.year || '').trim() !== this.year) {
        this.chosen = null;
        this.selectEl.textContent = '';
        this.selectEl.value = '';
        this.repaint();
      }

      this.renderModels();
      this.syncSubmit();
    }

    /* The models for the chosen year. The search box narrows these by
       hiding rows (applySearch), the way the header's does. */
    matching() {
      return this.models.filter(model => !this.year || (model.year || '').trim() === this.year);
    }

    renderModels() {
      this.visible = this.matching();
      this.listEl.textContent = '';

      /* The header picker's own row markup, class for class — renderList()
         and buildThumb() in assets/intex-compat-picker.js — so the two lists
         are styled by the same rules in assets/intex-compat-picker.css and
         cannot drift apart (§27). Only the data- hooks are ours. */
      this.visible.forEach((model, index) => {
        const option = document.createElement('li');
        option.className = 'intex-cp__option';
        const isSelected = Boolean(this.chosen && this.chosen.id === model.id && this.selectEl.value === model.id);
        option.id = `${this.controlEl.id}-opt-${index}`;
        option.setAttribute('role', 'option');
        option.setAttribute('aria-selected', isSelected ? 'true' : 'false');
        option.dataset.rangeOption = '';
        option.dataset.index = String(index);
        if (isSelected) option.classList.add('is-selected');

        option.appendChild(this.buildThumb(model));

        const text = document.createElement('span');
        text.className = 'intex-cp__option-text';

        const name = document.createElement('span');
        name.className = 'intex-cp__option-primary';
        name.textContent = label(model);
        text.appendChild(name);

        /* "28310-2 · 4.88m x 1.22m · 2019", dropping whichever part is blank —
           the header picker's number, then its buildSubtitle() line. */
        const parts = [model.number, model.size, model.year]
          .map(part => (part || '').trim())
          .filter(Boolean);

        if (parts.length) {
          const sub = document.createElement('span');
          sub.className = 'intex-cp__option-secondary t6';
          sub.textContent = parts.join(' · ');
          text.appendChild(sub);
        }

        option.appendChild(text);
        this.listEl.appendChild(option);
      });

      /* renderList() in the header: the search box only for a list longer
         than the threshold, and cleared when it is not shown. */
      const threshold = this.searchThreshold();
      const showSearch = threshold > 0 && this.visible.length > threshold;
      if (this.searchWrap) {
        this.searchWrap.hidden = !showSearch;
        if (!showSearch && this.searchInput) this.searchInput.value = '';
      }
      if (this.emptyEl) this.emptyEl.hidden = true;

      this.setActive(-1);
      if (!this.visible.length) this.closeList();
    }

    /* buildThumb() from assets/intex-compat-picker.js: the model's image from
       the data already loaded, or the no-photo glyph on the empty tile. */
    buildThumb(model) {
      const wrapper = document.createElement('span');
      wrapper.className = 'intex-cp__thumb';

      if (model.image) {
        const image = document.createElement('img');
        image.src = model.image;
        image.alt = '';
        image.loading = 'lazy';
        image.width = 40;
        image.height = 40;
        wrapper.appendChild(image);
      } else {
        const placeholder = this.noPhoto?.content.firstElementChild?.cloneNode(true);
        if (placeholder) {
          wrapper.classList.add('intex-cp__thumb--empty');
          wrapper.appendChild(placeholder);
        }
      }

      return wrapper;
    }

    syncSubmit() {
      this.submitEl.disabled = Boolean(this.navigating) || !this.selectEl.value;
    }

    /* The calm state between Show my parts and the next page (§14.77): the
       button shows Ignite's spinner and "Loading...", stays disabled, and the
       model field is locked so nothing can be changed half-way. Nothing else on
       the page moves. */
    setLoading(isLoading) {
      this.navigating = isLoading;
      this.submitEl.classList.toggle('is-loading', isLoading);
      this.submitEl.setAttribute('aria-busy', isLoading ? 'true' : 'false');
      if (this.controlEl) this.controlEl.disabled = isLoading;
      this.syncSubmit();
    }

    /* finder.write() — the finder's own save, so the entry lands in its own
       storage, sliced to its own limit — but without telling THIS page about
       it. write() announces the change on document, and two listeners act on
       it at once: compatibility-badge.js shows "Fits" badges on the current
       cards (which also moves the grid as cards grow), and intex-finder-cta.js
       repaints the header chip. Both are about to be thrown away by the
       navigation, so seeing them first is the flash. A capture listener on
       window runs before anything listening on document; stopping the event
       there keeps it from every listener on this page, for this one call. The
       next page reads the saved entry from storage as it loads, so its badges
       and chip render as normal. Other tabs still hear it through `storage`. */
    writeQuietly(finder, entries) {
      const swallow = event => event.stopImmediatePropagation();
      window.addEventListener(CHANGE_EVENT, swallow, true);
      try {
        finder.write(entries);
      } finally {
        window.removeEventListener(CHANGE_EVENT, swallow, true);
      }
    }

    /* ----------------------------------------------------------- NAVIGATION */

    /* The entry shape compatibility-finder.js onSave() builds, so what the
       panel saves is indistinguishable from what the header saves:
       read() keeps anything with `id` and an array of `filters`,
       compatibility-badge.js fits() reads `filters` alone, and buildName()
       works off filters[].label. See INTEX-CHANGES.md §14.37. */
    buildEntry(model) {
      const name = label(model);
      const subtitle = [model.size, model.year]
        .map(part => (part || '').trim())
        .filter(Boolean)
        .join(' · ');

      const entry = {
        id: `cf-${Date.now()}-${Math.round(Math.random() * 1000)}`,
        name,
        custom: false,
        filters: [{ param: MODEL_PARAM, value: model.id, label: name }],
        params: { model_title: name },
        url: this.resultsUrl
      };

      if (model.image) entry.image = model.image;
      if (subtitle) entry.subtitle = subtitle;

      return entry;
    }

    submit() {
      const gid = this.selectEl.value;
      if (!gid) return;

      const model = this.chosen && this.chosen.id === gid
        ? this.chosen
        : this.models.find(candidate => candidate.id === gid);
      if (!model) return;

      if (this.navigating) return;

      const finder = document.querySelector('compatibility-finder');
      const entry = this.buildEntry(model);

      this.setLoading(true);

      /* One compatibility system: the panel saves into the header finder's own
         entries through the finder's own read()/write(), and localStorage is
         never touched from here. The save is quiet (writeQuietly), so nothing
         on this page changes before it navigates; the new page shows the badges
         and the header chip from storage. renderList() is no longer called for
         the same reason — the finder's panel is closed and about to go. Then
         navigate with the finder's own buildUrl(), in the §14.32 order —
         current block setting, saved entry, data-all-url — so the panel and the
         header cannot disagree about the destination. */
      if (finder && typeof finder.read === 'function' && typeof finder.write === 'function') {
        const kept = finder.read().filter(candidate =>
          !(candidate.filters || []).some(filter => filter && filter.value === gid)
        );
        kept.push(entry);
        this.writeQuietly(finder, kept);

        if (typeof finder.buildUrl === 'function') {
          const base = finder.quickFilter?.dataset?.resultsUrl
            || entry.url
            || finder.dataset.allUrl;
          window.location.href = finder.buildUrl(base, entry);
          return;
        }
      }

      /* No finder on the page, or an older one without those methods: build
         the same URL ourselves rather than going nowhere. */
      const url = new URL(this.resultsUrl, window.location.origin);
      url.searchParams.set(MODEL_PARAM, gid);
      url.searchParams.set('model_title', entry.name);
      window.location.href = url.toString();
    }
  }

  if (!customElements.get('intex-range-picker')) {
    customElements.define('intex-range-picker', IntexRangePicker);
  }
})();